import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useApi, useMutation } from '../../lib/useApi.js';
import { api } from '../../lib/apiClient.js';
import { useClientPagination } from '../../lib/usePagination.js';
import { usePageMetadata } from '../../app/PageMetaContext.jsx';
import {
  PageHeader,
  Breadcrumbs,
  LoadingSkeleton,
  ErrorState,
  EmptyState,
  StatusChip,
  PageViewerControls,
} from '../../components/primitives.jsx';
import {
  toast,
  GaugeCard,
  KpiCard,
  StatusDonutMini,
  Sparkline,
  MiniBars,
} from '../../components/index.js';
import './dataAdmin.redesign.css';
import { ProvenanceBadge } from '../../components/ProvenanceBadge.jsx';

function humanize(value) {
  return String(value || '')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .trim();
}

// Connector-test check.status → executive-traffic-light tone.
const CHECK_TONE = {
  passed: 'sage',
  pass: 'sage',
  ok: 'sage',
  warning: 'amber',
  warn: 'amber',
  failed: 'brick',
  fail: 'brick',
  error: 'brick',
};

// Sync-run outcome → tone for the per-run outcome mix + row edge tint.
const RUN_TONE = {
  success: 'sage',
  succeeded: 'sage',
  warning: 'amber',
  partial: 'amber',
  error: 'brick',
  failed: 'brick',
  running: 'blue',
};

export function ConnectorDetailPage() {
  const { id } = useParams();
  const c = useApi(`/connectors/${id}`);
  const runs = useApi(`/connectors/${id}/sync-runs`);
  const testConnection = useMutation();
  const syncNow = useMutation();
  const retry = useMutation();
  const [latestTest, setLatestTest] = useState(null);
  const runPager = useClientPagination(runs.data?.data || [], {
    initialPageSize: 10,
    resetKey: id,
  });

  const data = c.data?.data;
  const activeTest = latestTest || data?.lastConnectionTest || null;
  const domainLabels = Array.isArray(data?.dataDomains)
    ? data.dataDomains.map((entry) => humanize(entry))
    : [];

  const allRuns = useMemo(() => runs.data?.data || [], [runs.data]);
  const health = data?.health || null;

  // Sync-run analytics — the page previously rendered only id/status/timestamps
  // and discarded durationSeconds + record counts. Surface them as trend charts.
  // Runs arrive newest-first; chart oldest→newest for a left-to-right timeline.
  const runAnalytics = useMemo(() => {
    const chrono = [...allRuns].reverse();
    const num = (v) => (typeof v === 'number' && Number.isFinite(v) ? v : null);
    const durations = chrono.map((r) => num(r.durationSeconds)).filter((v) => v != null);
    const updated = chrono.map((r) => num(r.recordsUpdated)).filter((v) => v != null);
    const errored = chrono.map((r) => num(r.recordsErrored)).filter((v) => v != null);
    const latestDuration = durations.length ? durations[durations.length - 1] : null;
    const totalUpdated = updated.reduce((a, v) => a + v, 0);
    const totalErrored = errored.reduce((a, v) => a + v, 0);
    return {
      durations,
      updated,
      errored,
      latestDuration,
      totalUpdated,
      totalErrored,
      hasDurations: durations.length > 0,
      hasRecords: updated.length > 0 || errored.length > 0,
    };
  }, [allRuns]);

  // Per-run outcome mix for the StatusDonutMini.
  const outcomeSegments = useMemo(() => {
    const counts = {};
    for (const r of allRuns) {
      const key = String(r.status || 'unknown').toLowerCase();
      counts[key] = (counts[key] || 0) + 1;
    }
    const order = ['success', 'succeeded', 'warning', 'partial', 'error', 'failed', 'running'];
    return Object.entries(counts)
      .sort((a, b) => order.indexOf(a[0]) - order.indexOf(b[0]))
      .map(([status, count]) => ({
        label: humanize(status) || status,
        count,
        tone: RUN_TONE[status] || 'slate',
      }));
  }, [allRuns]);

  // Readiness checks → pass/warn/fail tally for the score-bar + posture chips.
  const readiness = useMemo(() => {
    const checks = Array.isArray(activeTest?.checks) ? activeTest.checks : [];
    let pass = 0;
    let warn = 0;
    let fail = 0;
    for (const ch of checks) {
      const tone = CHECK_TONE[String(ch.status || '').toLowerCase()] || 'slate';
      if (tone === 'sage') pass += 1;
      else if (tone === 'amber') warn += 1;
      else if (tone === 'brick') fail += 1;
    }
    return { checks, pass, warn, fail, total: checks.length };
  }, [activeTest]);

  usePageMetadata({
    title: data?.name || 'Connector',
    subtitle: 'Connector diagnostics, readiness checks, and sync activity for DHA PRIME.',
  });

  async function runConnectionTest() {
    try {
      const result = await testConnection.run(() => api.post(`/connectors/${id}/test`, {}));
      setLatestTest(result.data || null);
      await c.refresh?.();
      toast.success({
        title: 'Prototype adapter validated',
        body: `${c.data?.data?.name || id} returned simulated adapter diagnostics for review. No external system was contacted.`,
      });
    } catch (error) {
      toast.error({ title: 'Prototype validation failed', body: error.message });
    }
  }

  async function triggerSync() {
    try {
      const result = await syncNow.run(() => api.post(`/connectors/${id}/sync`, {}));
      await Promise.all([c.refresh?.(), runs.refresh?.()]);
      toast.success({
        title: 'Simulated sync complete',
        body: `${c.data?.data?.name || id} recorded simulation ${result.data?.id || 'SYNC-NEW'} without contacting an external system.`,
      });
    } catch (error) {
      toast.error({ title: 'Sync failed', body: error.message });
    }
  }

  async function retryRun(rid) {
    try {
      await retry.run(() => api.post(`/sync-runs/${rid}/retry`, {}));
      await runs.refresh?.();
      toast.success({ title: 'Simulation retried', body: `A simulated retry was recorded for ${rid}.` });
    } catch (error) {
      toast.error({ title: 'Retry failed', body: error.message });
    }
  }

  if (c.loading)
    return (
      <>
        <PageHeader title="Connector" />
        <LoadingSkeleton rows={5} />
      </>
    );
  if (c.error) return <ErrorState error={c.error} />;
  if (!data) return <EmptyState title="Connector not found" />;

  return (
    <>
      <Breadcrumbs
        items={[{ to: '/admin/data', label: 'Data Integration' }, { label: data.name }]}
      />
      <PageHeader
        eyebrow="Connector"
        title={data.name}
        subtitle={`${data.sourceSystem || data.system || ''} · ${data.id}`}
        actions={
          <>
            <button
              className="btn ghost sm"
              type="button"
              onClick={runConnectionTest}
              disabled={testConnection.running}
            >
              {testConnection.running ? 'Validating…' : 'Validate prototype adapter'}
            </button>
            <button
              className="btn primary sm tactile-press"
              type="button"
              onClick={triggerSync}
              disabled={syncNow.running}
            >
              {syncNow.running ? 'Simulating…' : 'Simulate sync'}
            </button>
          </>
        }
      >
        <StatusChip status={data.status} />
      </PageHeader>

      {health ? (
        <section
          className="connector-health-hero glass-panel dark"
          aria-label="Connector health"
          data-testid="connector-health-hero"
        >
          <div className="connector-health-hero__identity">
            <span className="eyebrow">Connector profile · Prototype posture</span>
            <h2 className="connector-health-hero__name">{data.name}</h2>
            <div className="connector-health-hero__meta">
              <StatusChip status={data.status} />
              {data.refreshCadence ? (
                <span className="chip outline sm">{humanize(data.refreshCadence)}</span>
              ) : null}
            </div>
            <dl className="connector-health-hero__sla">
              {health.lastSuccessAt ? (
                <div>
                  <dt>Last recorded simulated outcome</dt>
                  <dd>{new Date(health.lastSuccessAt).toLocaleString()}</dd>
                </div>
              ) : null}
              {health.lastWarningAt ? (
                <div>
                  <dt>Last warning</dt>
                  <dd>{new Date(health.lastWarningAt).toLocaleString()}</dd>
                </div>
              ) : null}
            </dl>
          </div>

          {typeof health.availability === 'number' ? (
            <GaugeCard
              id="connector-availability"
              value={Math.round(health.availability * 1000) / 10}
              min={0}
              max={100}
              unit="%"
              label="Availability"
              sublabel="Recorded prototype outcome ratio"
              tone={
                health.availability >= 0.99
                  ? 'sage'
                  : health.availability >= 0.95
                    ? 'amber'
                    : 'brick'
              }
            />
          ) : null}

          {typeof health.latencyMsP95 === 'number' ? (
            <GaugeCard
              id="connector-latency"
              value={health.latencyMsP95}
              min={0}
              max={Math.max(1000, Math.ceil((health.latencyMsP95 * 1.5) / 100) * 100)}
              unit="ms"
              label="Latency p95"
              sublabel="Prototype projection"
              tone={
                health.latencyMsP95 <= 500
                  ? 'sage'
                  : health.latencyMsP95 <= 1000
                    ? 'amber'
                    : 'brick'
              }
            />
          ) : null}
        </section>
      ) : null}

      {health && typeof health.openIssueCount === 'number' ? (
        <div style={{ marginBottom: 'var(--space-4, 1rem)', maxWidth: 320 }}>
          <KpiCard
            id="open-issues"
            surface="connector"
            label="Open data-steward issues"
            value={health.openIssueCount}
            status={
              health.openIssueCount === 0 ? 'sage' : health.openIssueCount > 2 ? 'brick' : 'amber'
            }
            statusLabel={health.openIssueCount === 0 ? 'Clear' : 'Needs follow-up'}
            footer="Follow-up issues raised by recorded prototype outcomes"
          />
        </div>
      ) : null}

      <section className="bento">
        <div className="span-5 card">
          <div className="eyebrow">Connector profile</div>
          <h2 className="card-title">Sandbox integration posture</h2>
          <dl className="briefing-provenance">
            <div>
              <dt>Maturity</dt>
              <dd>{humanize(data.maturityLevel) || 'Not set'}</dd>
            </div>
            <div>
              <dt>Environment mode</dt>
              <dd>{data.environmentMode || 'Not set'}</dd>
            </div>
            <div>
              <dt>Mapping version</dt>
              <dd>{data.fieldMappingsVersion || 'Not registered'}</dd>
            </div>
            <div>
              <dt>Credential mode</dt>
              <dd>{data.credentialMode || 'Not set'}</dd>
            </div>
          </dl>
          <div style={{ marginTop: 16 }}>
            <div className="eyebrow">Domain coverage</div>
            <div className="briefing-artifact-chip-row" style={{ marginTop: 8 }}>
              {domainLabels.length ? (
                domainLabels.map((domain) => (
                  <span key={domain} className="chip outline sm">
                    {domain}
                  </span>
                ))
              ) : (
                <span className="muted small">No domains declared</span>
              )}
            </div>
          </div>
        </div>
        <div className="span-7 card" data-testid="connector-test-result">
          <div className="row spread">
            <div>
              <div className="eyebrow">Adapter readiness</div>
              <h2 className="card-title">Prototype-adapter diagnostics</h2>
            </div>
            {activeTest ? <StatusChip status={activeTest.status} /> : null}
          </div>
          {!activeTest ? (
            <EmptyState
              title="No prototype validation yet"
              description="Validate the prototype adapter configuration and simulated checks without contacting an external system."
            />
          ) : (
            <>
              <ProvenanceBadge value={activeTest} kind="connector" />
              <p className="muted small">
                Simulated validation recorded at{' '}
                {activeTest.testedAt
                  ? new Date(activeTest.testedAt).toLocaleString()
                  : 'unknown time'}{' '}
                by {activeTest.triggeredByUserId || 'unknown actor'}.
              </p>
              {readiness.total ? (
                <div data-testid="connector-readiness-scorebar">
                  <div className="connector-readiness__summary">
                    <span className="data-admin__rollup-sub">
                      Readiness: {readiness.pass}/{readiness.total} checks passing
                    </span>
                    {readiness.fail ? (
                      <span className="data-admin__rollup-sub">{readiness.fail} failing</span>
                    ) : readiness.warn ? (
                      <span className="data-admin__rollup-sub">{readiness.warn} need review</span>
                    ) : null}
                  </div>
                  <div
                    className="connector-readiness__scorebar"
                    role="img"
                    aria-label={`Readiness checks: ${readiness.pass} passing, ${readiness.warn} warning, ${readiness.fail} failing of ${readiness.total}`}
                  >
                    {readiness.pass ? (
                      <span
                        className="pass"
                        style={{ width: `${(readiness.pass / readiness.total) * 100}%` }}
                      />
                    ) : null}
                    {readiness.warn ? (
                      <span
                        className="warn"
                        style={{ width: `${(readiness.warn / readiness.total) * 100}%` }}
                      />
                    ) : null}
                    {readiness.fail ? (
                      <span
                        className="fail"
                        style={{ width: `${(readiness.fail / readiness.total) * 100}%` }}
                      />
                    ) : null}
                  </div>
                  <div className="connector-posture-chips">
                    {readiness.checks.map((check) => {
                      const tone = CHECK_TONE[String(check.status || '').toLowerCase()] || 'slate';
                      const checkLabel = check.label || humanize(check.id);
                      return (
                        <span
                          key={`posture-${check.id}`}
                          className="connector-posture-chip"
                          data-tone={tone}
                          title={`${checkLabel}: ${check.status}`}
                          aria-label={`${checkLabel}: ${check.status}`}
                        >
                          <span className="connector-posture-chip__dot" aria-hidden />
                          {humanize(check.status)}
                        </span>
                      );
                    })}
                  </div>
                </div>
              ) : null}
              <div className="briefing-artifact-chip-row" style={{ margin: '12px 0' }}>
                {(activeTest.adapterFunctions || []).map((fnName) => (
                  <span key={fnName} className="chip outline sm">
                    {fnName}
                  </span>
                ))}
              </div>
              <table className="table">
                <thead>
                  <tr>
                    <th>Check</th>
                    <th>Status</th>
                    <th>Detail</th>
                  </tr>
                </thead>
                <tbody>
                  {(activeTest.checks || []).map((check) => (
                    <tr key={check.id}>
                      <td>{check.label || humanize(check.id)}</td>
                      <td>
                        <StatusChip status={check.status} />
                      </td>
                      <td>{check.detail || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </div>
        <div className="span-12 card">
          <div className="row spread">
            <h2 className="card-title">Sync runs</h2>
            <StatusChip status={data.status} />
          </div>
          {(runs.data?.data || []).length === 0 ? (
            <EmptyState title="No sync runs" />
          ) : (
            <>
              {runAnalytics.hasDurations || runAnalytics.hasRecords || outcomeSegments.length ? (
                <div className="connector-sync-analytics" data-testid="connector-sync-analytics">
                  {runAnalytics.hasDurations ? (
                    <div className="connector-sync-metric">
                      <div className="connector-sync-metric__head">
                        <span className="connector-sync-metric__label">Simulated duration</span>
                        <span className="connector-sync-metric__value">
                          {runAnalytics.latestDuration}s
                        </span>
                      </div>
                      <div className="connector-sync-metric__chart">
                        <Sparkline
                          values={runAnalytics.durations}
                          tone="blue"
                          height={36}
                          ariaLabel="Sync-run duration trend in seconds"
                        />
                      </div>
                      <span className="connector-sync-metric__foot">
                        Projected seconds per simulation · oldest → newest
                      </span>
                    </div>
                  ) : null}
                  {runAnalytics.hasRecords ? (
                    <div className="connector-sync-metric">
                      <div className="connector-sync-metric__head">
                        <span className="connector-sync-metric__label">Projected record updates</span>
                        <span className="connector-sync-metric__value">
                          {runAnalytics.totalUpdated}
                        </span>
                      </div>
                      <div className="connector-sync-metric__chart">
                        <MiniBars
                          values={runAnalytics.updated}
                          tone="sage"
                          height={36}
                          ariaLabel="Projected records updated per simulated run"
                        />
                      </div>
                      <span className="connector-sync-metric__foot">
                        Simulated projections per run
                      </span>
                    </div>
                  ) : null}
                  {runAnalytics.hasRecords ? (
                    <div className="connector-sync-metric">
                      <div className="connector-sync-metric__head">
                        <span className="connector-sync-metric__label">Projected record errors</span>
                        <span className="connector-sync-metric__value">
                          {runAnalytics.totalErrored}
                        </span>
                      </div>
                      <div className="connector-sync-metric__chart">
                        <MiniBars
                          values={runAnalytics.errored}
                          tone={runAnalytics.totalErrored ? 'brick' : 'slate'}
                          height={36}
                          ariaLabel="Records errored per sync run"
                        />
                      </div>
                      <span className="connector-sync-metric__foot">
                        Simulated error projections per run
                      </span>
                    </div>
                  ) : null}
                  {outcomeSegments.length ? (
                    <div className="connector-sync-outcome" data-testid="connector-outcome-mix">
                      <StatusDonutMini
                        segments={outcomeSegments}
                        size={88}
                        totalLabel="Runs"
                        ariaLabel="Sync-run outcome mix"
                      />
                      <div className="connector-sync-outcome__copy">
                        <span className="connector-sync-metric__label">Outcome mix</span>
                        <span className="connector-sync-metric__foot">
                          Across {allRuns.length} recorded simulations
                        </span>
                      </div>
                    </div>
                  ) : null}
                </div>
              ) : null}
              <div className="connector-runs__table-wrap">
              <table className="table connector-runs__table">
                <thead>
                  <tr>
                    <th>Run</th>
                    <th>Status</th>
                    <th>Simulated projection</th>
                    <th>Duration</th>
                    <th>Started</th>
                    <th>Completed</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {runPager.items.map((r) => {
                    const outcome = String(r.status || '').toLowerCase();
                    const hasRecordStats =
                      typeof r.recordsUpdated === 'number' || typeof r.recordsErrored === 'number';
                    return (
                      <tr key={r.id} data-outcome={outcome}>
                        <td>{r.id}</td>
                        <td>
                          <StatusChip status={r.status} />
                          <div style={{ marginTop: 4 }}>
                            <ProvenanceBadge value={r} kind="connector" />
                          </div>
                        </td>
                        <td className="connector-runs__records">
                          {typeof r.projectedRecords === 'number' ? (
                            <span>{r.projectedRecords} projected</span>
                          ) : hasRecordStats ? (
                            <>
                              <span>{r.recordsUpdated ?? 0} projected updates</span>
                              {r.recordsErrored ? (
                                <div className="connector-runs__records-sub">
                                  {r.recordsErrored} projected errors
                                </div>
                              ) : null}
                            </>
                          ) : (
                            '—'
                          )}
                        </td>
                        <td>
                          {typeof r.durationSeconds === 'number' ? `${r.durationSeconds}s` : '—'}
                        </td>
                        <td>{r.startedAt ? new Date(r.startedAt).toLocaleString() : '—'}</td>
                        <td>{r.completedAt ? new Date(r.completedAt).toLocaleString() : '—'}</td>
                        <td>
                          <button
                            className="btn ghost sm"
                            onClick={() => retryRun(r.id)}
                            disabled={retry.running}
                          >
                            Retry simulation
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              </div>
            </>
          )}
          <PageViewerControls
            page={runPager.page}
            label="sync runs"
            pageSize={runPager.pageSize}
            onOffsetChange={runPager.setOffset}
            onPageSizeChange={runPager.setPageSize}
          />
        </div>
      </section>
    </>
  );
}
